// Data table on TanStack Table v9 and TanStack Virtual: server-side sort with aria-sort, sticky header, group rows (optionally with a badge after the label, such as a section's priority), row click, infinite loading, and a focusable scroll region for tables without interactive rows.
import {
  createColumnHelper,
  rowSortingFeature,
  tableFeatures,
  useTable,
  type ColumnDef,
  type OnChangeFn,
  type SortingState,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import { ArrowDownIcon, ArrowUpDownIcon, ArrowUpIcon } from "lucide-react";
import { useEffect, useMemo, useRef, type ReactNode } from "react";

import { cn } from "@/lib/cn";

export const dataTableFeatures = tableFeatures({ rowSortingFeature });
export type DataTableFeatures = typeof dataTableFeatures;
export type DataTableColumn<TData extends object> = ColumnDef<DataTableFeatures, TData>;
export type { SortingState };

export function dataTableColumnHelper<TData extends object>() {
  return createColumnHelper<DataTableFeatures, TData>();
}

type DisplayItem<TData> =
  | { kind: "group"; label: string; key: string; badge: ReactNode }
  | { kind: "row"; index: number; data: TData; id: string };

interface DataTableProps<TData extends object> {
  data: readonly TData[];
  columns: DataTableColumn<TData>[];
  getRowId: (row: TData) => string;
  caption: string;
  total?: number;
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  groupBy?: (row: TData) => string | null;
  groupBadge?: (row: TData) => ReactNode;
  onRowClick?: (row: TData) => void;
  selectedRowId?: string | null;
  rowHeight?: number;
  onEndReached?: () => void;
  emptyState?: ReactNode;
  footer?: ReactNode;
  focusable?: boolean;
  className?: string;
}

const ARIA_SORT = { asc: "ascending", desc: "descending" } as const;

export function DataTable<TData extends object>({
  data,
  columns,
  getRowId,
  caption,
  total,
  sorting,
  onSortingChange,
  groupBy,
  groupBadge,
  onRowClick,
  selectedRowId,
  rowHeight = 46,
  onEndReached,
  emptyState,
  footer,
  focusable = false,
  className,
}: DataTableProps<TData>) {
  const table = useTable({
    features: dataTableFeatures,
    columns,
    data,
    getRowId: (row) => getRowId(row),
    manualSorting: true,
    enableSortingRemoval: false,
    ...(sorting === undefined ? {} : { state: { sorting } }),
    ...(onSortingChange === undefined ? {} : { onSortingChange }),
  });

  const rows = table.getRowModel().rows;
  const items = useMemo<DisplayItem<TData>[]>(() => {
    const list: DisplayItem<TData>[] = [];
    let previous: string | null = null;
    rows.forEach((row, index) => {
      const label = groupBy ? groupBy(row.original) : null;
      if (label !== null && label !== previous) {
        list.push({
          kind: "group",
          label,
          key: `group-${label}-${index}`,
          badge: groupBadge?.(row.original),
        });
      }
      previous = label;
      list.push({ kind: "row", index, data: row.original, id: row.id });
    });
    return list;
  }, [rows, groupBy, groupBadge]);

  const scrollRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: (index) => (items[index]?.kind === "group" ? 32 : rowHeight),
    overscan: 12,
  });
  const virtualItems = virtualizer.getVirtualItems();
  const paddingTop = virtualItems[0]?.start ?? 0;
  const paddingBottom = virtualizer.getTotalSize() - (virtualItems.at(-1)?.end ?? 0);
  const lastIndex = virtualItems.at(-1)?.index ?? -1;

  useEffect(() => {
    if (onEndReached !== undefined && items.length > 0 && lastIndex >= items.length - 15) {
      onEndReached();
    }
  }, [lastIndex, items.length, onEndReached]);

  const headerGroups = table.getHeaderGroups();
  const columnCount = table.getAllLeafColumns().length;

  return (
    <div
      ref={scrollRef}
      className={cn("relative min-h-0 pane-scroll", className)}
      {...(focusable ? { tabIndex: 0, role: "region", "aria-label": caption } : {})}
    >
      <table
        className="w-full border-collapse text-ui tabular-nums"
        aria-rowcount={(total ?? data.length) + headerGroups.length}
      >
        <caption className="sr-only">{caption}</caption>
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr key={headerGroup.id} aria-rowindex={1}>
              {headerGroup.headers.map((header) => {
                const sorted = header.column.getIsSorted();
                const canSort = header.column.getCanSort() && onSortingChange !== undefined;
                const content = header.isPlaceholder ? null : <table.FlexRender header={header} />;
                return (
                  <th
                    key={header.id}
                    scope="col"
                    colSpan={header.colSpan}
                    aria-sort={canSort ? (sorted === false ? "none" : ARIA_SORT[sorted]) : undefined}
                    className="sticky top-0 z-(--z-sticky) border-b bg-card px-3.5 py-2.5 text-left text-xs font-medium whitespace-nowrap text-muted-foreground"
                  >
                    {canSort ? (
                      <button
                        type="button"
                        onClick={header.column.getToggleSortingHandler()}
                        className="inline-flex items-center gap-1 border-0 bg-transparent p-0 font-medium text-inherit hover:text-foreground"
                      >
                        {content}
                        {sorted === "asc" ? (
                          <ArrowUpIcon aria-hidden className="size-[13px]" />
                        ) : sorted === "desc" ? (
                          <ArrowDownIcon aria-hidden className="size-[13px]" />
                        ) : (
                          <ArrowUpDownIcon aria-hidden className="size-[13px] opacity-60" />
                        )}
                      </button>
                    ) : (
                      content
                    )}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {paddingTop > 0 ? (
            <tr aria-hidden>
              <td colSpan={columnCount} style={{ height: paddingTop, padding: 0 }} />
            </tr>
          ) : null}
          {virtualItems.map((virtualItem) => {
            const item = items[virtualItem.index];
            if (item === undefined) {
              return null;
            }
            if (item.kind === "group") {
              return (
                <tr key={item.key} data-index={virtualItem.index} ref={virtualizer.measureElement}>
                  <th
                    scope="colgroup"
                    colSpan={columnCount}
                    className="bg-accent px-3.5 py-[7px] text-left text-xs font-semibold text-brand-blue"
                  >
                    {item.badge === undefined || item.badge === null ? (
                      item.label
                    ) : (
                      <span className="inline-flex items-center gap-2">
                        {item.label}
                        {item.badge}
                      </span>
                    )}
                  </th>
                </tr>
              );
            }
            const row = rows[item.index];
            if (row === undefined) {
              return null;
            }
            const clickable = onRowClick !== undefined;
            return (
              <tr
                key={item.id}
                data-index={virtualItem.index}
                ref={virtualizer.measureElement}
                aria-rowindex={item.index + headerGroups.length + 1}
                aria-current={selectedRowId === item.id ? "true" : undefined}
                tabIndex={clickable ? 0 : undefined}
                onClick={
                  clickable
                    ? () => {
                        onRowClick(item.data);
                      }
                    : undefined
                }
                onKeyDown={
                  clickable
                    ? (event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          onRowClick(item.data);
                        }
                      }
                    : undefined
                }
                className={cn(
                  "transition-colors duration-120",
                  clickable && "cursor-pointer hover:bg-accent",
                  selectedRowId === item.id && "bg-selected",
                )}
              >
                {row.getAllCells().map((cell) => (
                  <td key={cell.id} className="border-b border-border-subtle px-3.5 py-3 align-middle">
                    <table.FlexRender cell={cell} />
                  </td>
                ))}
              </tr>
            );
          })}
          {paddingBottom > 0 ? (
            <tr aria-hidden>
              <td colSpan={columnCount} style={{ height: paddingBottom, padding: 0 }} />
            </tr>
          ) : null}
        </tbody>
      </table>
      {data.length === 0 && emptyState ? emptyState : null}
      {footer}
    </div>
  );
}
